import type {
  AnalysisOptions,
  AnalysisTypeKey,
  DataRow,
  VariableMapping,
} from '../stores/systems/keisuke/dashboardStore';
import { buildDrTypeSwitchPatch, normalizeDrType } from './keisukeDoseResponseDefaults';
import { gridDataHasNumericValues } from './keisukeGridData';

/** 空グリッド切替時のコンパクト表用（フォーム i18n 既定と揃える英語キー） */
const EMPTY_DOSE_HEADERS = ['Dose', 'Effect', 'Total'] as const;
const EMPTY_SURVIVAL_HEADERS = ['Group', 'Time', 'Event'] as const;

function pickColumns(headers: string[]): {
  col0: string;
  col1: string;
  col2: string;
  col3: string;
} {
  const h = headers.filter((x) => String(x).trim() !== '');
  return {
    col0: h[0] ?? 'group',
    col1: h[1] ?? 'value',
    col2: h[2] ?? h[1] ?? 'value',
    col3: h[3] ?? h[2] ?? h[1] ?? 'value',
  };
}

function mappingForType(type: AnalysisTypeKey, headers: string[]): VariableMapping {
  const { col0, col1, col2, col3 } = pickColumns(headers);
  switch (type) {
    case 'basic-stats':
      return { valueColumn: col1 };
    case 'qpcr':
      return { groupColumn: col0, beforeColumn: col1, valueColumn: col2 };
    case 'longitudinal':
      return {
        subjectColumn: col0,
        groupColumn: col1,
        timeColumn: col2,
        valueColumn: col3,
      };
    case 'regression':
      return { doseColumn: col0, valueColumn: col1 };
    case 'bar-chart':
      return { groupColumn: col0, valueColumn: col1 };
    case 'dose-response':
      return {
        doseColumn: col0,
        effectColumn: col1,
        totalColumn: col2?.trim() ? col2 : 'Total',
      };
    case 'survival':
      return {
        survivalGroupColumn: col0,
        groupColumn: col0,
        timeColumn: col1,
        eventColumn: col2,
      };
    case 'anova':
      return { factorAColumn: col0, valueColumn: col1 };
    case 't-test':
    default:
      return { groupColumn: col0, valueColumn: col1 };
  }
}

/**
 * 解析タイプ切替時に列マッピング等をヘッダーに合わせて補完する。
 * 既存 mapping のキーは維持し、空のキーのみ埋める。
 * 空グリッドで用量反応／生存へ切替時は、前解析の group/value ヘッダを継がない。
 */
export function buildAnalysisSwitchPatch(
  nextType: AnalysisTypeKey,
  gridHeaders: string[],
  current: AnalysisOptions,
  gridData?: DataRow[]
): Partial<AnalysisOptions> {
  const empty =
    gridData != null && !gridDataHasNumericValues(gridHeaders, gridData);
  const headersForMapping =
    empty && nextType === 'dose-response'
      ? [...EMPTY_DOSE_HEADERS]
      : empty && nextType === 'survival'
        ? [...EMPTY_SURVIVAL_HEADERS]
        : gridHeaders;

  const defaults = mappingForType(nextType, headersForMapping);
  /** 空のコンパクト解析は前タイプの mapping を持ち込まない（group→dose 汚染防止） */
  const skipPrevOverlay =
    empty && (nextType === 'dose-response' || nextType === 'survival');
  const mapping: VariableMapping = { ...defaults };
  if (!skipPrevOverlay) {
    const prev = current.mapping ?? {};
    for (const key of Object.keys(prev) as (keyof VariableMapping)[]) {
      const v = prev[key];
      if (v != null && String(v).trim() !== '') {
        mapping[key] = v;
      }
    }
  }

  const patch: Partial<AnalysisOptions> = {
    type: nextType,
    mapping,
  };

  if (nextType === 'anova' && !current.anovaType) {
    patch.anovaType = 'oneWay';
  }
  /** 未設定のまま「なし」表示＋自動 Tukey 結果、の食い違いを避ける */
  if (nextType === 'anova' && current.postHoc == null) {
    patch.postHoc = 'tukey';
  }
  if (nextType === 'qpcr' && current.qpcrMethod == null) {
    patch.qpcrMethod = 'deltaCt';
  }
  if (nextType === 'regression') {
    if (current.regressionMode == null) patch.regressionMode = 'scatter';
    if (current.regressionType == null) patch.regressionType = 'standard';
  }
  if (nextType === 'dose-response') {
    Object.assign(
      patch,
      buildDrTypeSwitchPatch(normalizeDrType(current.drType), { ...current, mapping })
    );
  }

  return patch;
}
